import { useEffect, useRef, useState } from 'react'

import { axiosInstance } from '../../../service/axiosManager'

const API = process.env.REACT_APP_HOST_API

// The Monitor's live rate (TelemetryDataProvider): client telemetry is batched every 5 s, so
// polling faster buys nothing.
export const OVERVIEW_POLL_MS = 20000

// How often each condition's telemetry is fetched in full again (afterId=0), the answer
// replacing what is held. A poll asks only for the batches after the mark, the largest row id
// held, but a row's id is given when its save starts: of two batches saved at once, the one
// with the smaller id can be committed after a poll has taken the other, and no later poll has
// it. The full fetch brings it back within this long.
export const FULL_FETCH_MS = 5 * 60 * 1000

// What the hook holds before anything has answered.
export const NO_TELEMETRY = {
  eventsBySession: {},
  rosterBySession: {},
  fetchedAtBySession: {},
  failedBySession: {},
  lastIdBySession: {},
  fullAtBySession: {},
  loaded: false,
}

const timeOf = (event) => {
  const time = Number(event?.timestamp)
  return Number.isFinite(time) ? time : 0
}

// `events` in time order: the array itself when it already is, else a sorted copy - stable, so
// events of the same millisecond keep the order they came in.
const inTimeOrder = (events) => {
  for (let i = 1; i < events.length; i += 1) {
    if (timeOf(events[i]) < timeOf(events[i - 1])) return events.slice().sort((a, b) => timeOf(a) - timeOf(b))
  }
  return events
}

// `events` without the ones whose event_id is in `seen` or came earlier in `events`: a study
// condition's page numbers every event, and a batch it sends twice (resent after a timeout the
// server in fact survived) keeps its ids. Events without one (regular sessions') all stay.
const withoutCopies = (events, seen) =>
  events.filter((event) => {
    const id = event?.event_id
    if (id == null) return true
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })

// The index of the first of `held`'s events (in time order) at or after `time`.
const firstAtOrAfter = (held, time) => {
  let low = 0
  let high = held.length
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (timeOf(held[middle]) < time) low = middle + 1
    else high = middle
  }
  return low
}

/**
 * A student's events once an answer has come: `held`, in time order, with the answer's `fresh`
 * ones (in the order the server sent them: by batch) put in among them, in time order - those
 * of the same millisecond after the ones held. An event whose event_id is already there is left
 * out. Returns `held` itself when nothing is new.
 */
export const mergeEvents = (held = [], fresh = []) => {
  if (!fresh.length) return held
  const sorted = inTimeOrder(fresh)
  // A copy has its original's timestamp, so only the held events from the earliest new one on
  // can be the original of one.
  const seen = new Set()
  for (let i = firstAtOrAfter(held, timeOf(sorted[0])); i < held.length; i += 1) {
    if (held[i]?.event_id != null) seen.add(held[i].event_id)
  }
  const added = withoutCopies(sorted, seen)
  if (!added.length) return held
  if (!held.length) return added
  // Most polls: only events after every one held.
  if (timeOf(added[0]) >= timeOf(held[held.length - 1])) return held.concat(added)
  // A batch that was late (a page that was offline, a resent one): in among them.
  const merged = []
  let i = 0
  let j = 0
  while (i < held.length || j < added.length) {
    if (j >= added.length || (i < held.length && timeOf(held[i]) <= timeOf(added[j]))) merged.push(held[i++])
    else merged.push(added[j++])
  }
  return merged
}

// A session's events by user once an answer has come: `held` with the answer's eventsByUser
// merged in (mergeEvents), `held` itself when no student has anything new, and a student with
// nothing new keeps the same array.
const mergeSession = (held = {}, fresh = {}) => {
  let next = held
  Object.entries(fresh).forEach(([userId, events]) => {
    if (!Array.isArray(events) || !events.length) return
    const merged = mergeEvents(held[userId], events)
    if (merged === held[userId] || !merged.length) return
    if (next === held) next = { ...held }
    next[userId] = merged
  })
  return next
}

// What the hook holds after a round of requests. `answers`: [{ id, events, afterId, lastId,
// roster }], one per session. `events`: the telemetry the answer brought (its eventsByUser),
// null when that request failed. `afterId`: the session's mark the request was sent with - 0
// asks for all of its telemetry, which replaces what is held, not merged into it (a first
// request, and the full fetch every FULL_FETCH_MS); a later one asks only for what came after
// the held mark, which is merged into what is held (mergeEvents), and an answer from another
// mark than the one held is not used. `lastId`: the answer's mark, for the next request.
// `roster`: null when that request failed. `sentAt`: when the round's requests were sent. A
// failed request keeps that session's last data and mark (and when it was last fetched in full)
// and marks the session failed; a round in which every request failed (the network is down)
// does not make the class loaded.
export const nextTelemetry = (previous, { answers, sentAt }) => {
  const eventsBySession = { ...previous.eventsBySession }
  const rosterBySession = { ...previous.rosterBySession }
  const fetchedAtBySession = { ...previous.fetchedAtBySession }
  const failedBySession = { ...previous.failedBySession }
  const lastIdBySession = { ...previous.lastIdBySession }
  const fullAtBySession = { ...previous.fullAtBySession }
  answers.forEach(({ id, events, afterId = 0, lastId = null, roster }) => {
    const used = Boolean(events) && (!afterId || afterId === lastIdBySession[id])
    if (used) {
      eventsBySession[id] = mergeSession(afterId ? eventsBySession[id] : {}, events)
      lastIdBySession[id] = Math.max(afterId, Number(lastId) || 0)
      fetchedAtBySession[id] = sentAt
      if (!afterId) fullAtBySession[id] = sentAt
    }
    if (roster) rosterBySession[id] = roster
    failedBySession[id] = !used || !roster
  })
  return {
    eventsBySession,
    rosterBySession,
    fetchedAtBySession,
    failedBySession,
    lastIdBySession,
    fullAtBySession,
    loaded: previous.loaded || answers.some(({ events, roster }) => events || roster),
  }
}

// What the hook holds for the sessions in `ids` only: a session that has left the set loses
// its data and its mark, so if it comes back it starts afresh. `held` itself when none has left;
// not loaded when nothing held is left.
export const onlySessions = (held, ids) => {
  const kept = new Set(ids.map(String))
  const fields = [
    'eventsBySession',
    'rosterBySession',
    'fetchedAtBySession',
    'failedBySession',
    'lastIdBySession',
    'fullAtBySession',
  ]
  if (fields.every((field) => Object.keys(held[field] || {}).every((id) => kept.has(id)))) return held
  const next = { ...held }
  fields.forEach((field) => {
    next[field] = Object.fromEntries(Object.entries(held[field] || {}).filter(([id]) => kept.has(id)))
  })
  next.loaded = held.loaded && [next.eventsBySession, next.rosterBySession].some((byId) => Object.keys(byId).length > 0)
  return next
}

// The mark a round sent at `sentAt` asks for session `id`'s telemetry from, given what the hook
// holds (`held`): 0 - all of it, the answer replacing what is held - when nothing is held of it
// (no mark), and again on the round nearest FULL_FETCH_MS after its last full fetch was sent
// (due from half a poll before: the timer's ticks come a few ms early or late, and waiting for
// the round after would make it a poll late); else its mark, for what is new. `fullFailed`: its
// last request asked for all of it and failed - this one asks only for what is new, so its
// students keep updating while a full answer cannot be had, and the next asks for all again.
export const afterIdFor = (held, id, sentAt, fullFailed = false) => {
  const mark = held.lastIdBySession[id] || 0
  if (!mark) return 0
  const fullAt = held.fullAtBySession?.[id]
  const due = fullAt == null || sentAt - fullAt >= FULL_FETCH_MS - OVERVIEW_POLL_MS / 2
  return due && !fullFailed ? 0 : mark
}

// An answer of the overview route ({ eventsByUser, lastId }) as { events, lastId }, or null
// when it is not one. An answer to a request from a mark must give the next one: without it,
// what it brought would come again.
const overviewAnswer = (data, afterId) => {
  const events = data?.eventsByUser
  if (!events || typeof events !== 'object' || Array.isArray(events)) return null
  const lastId = Number.isSafeInteger(data.lastId) && data.lastId >= 0 ? data.lastId : null
  if (afterId && lastId === null) return null
  return { events, lastId }
}

// What the hook last held, kept when its component unmounts: the experiment page shows one tab
// at a time, and Show in Presenter leaves the Overview for the Presenter. Opened again, the
// Overview starts from it - the class as of the last answer, and each condition's mark, so its
// first round asks only for what is new - instead of from nothing, behind spinners until every
// condition has answered. A mount takes only its own conditions from it (onlySessions), so
// another experiment's go at its first answer; signing out reloads the page.
let keptTelemetry = NO_TELEMETRY

// Forgets what the hook has kept: its next mount starts from nothing. For tests.
export const forgetKeptTelemetry = () => {
  keptTelemetry = NO_TELEMETRY
}

// The session ids in a set's key (useConditionTelemetry's).
const idsOf = (key) => (key ? key.split(',').map(Number) : [])

// Every live condition's telemetry and roster, one request of each per condition every 20 s
// while the page is visible, and at once when it becomes visible again. The telemetry comes
// from the overview route (GET /telemetry/session/:id/overview), which sends only the event
// types the Overview reads (edits without the copy of the file each one carries; a run keeps
// its code), and only the batches after a mark - the largest telemetry row id the page has:
// a condition's first request asks for everything (afterId=0), each later one for what came
// after its mark (its answer's lastId), merged into what is held (nextTelemetry), and every
// FULL_FETCH_MS one asks for everything again, which replaces what is held (afterIdFor): a
// batch committed after a poll took a later one is back then. Nothing else: the offloading
// summary (the AI's labels) stays on the Compare conditions tab. A request that fails keeps
// that condition's last data and mark (getTelemetryEventsBySession / getAllUsersInSession would
// turn it into an empty class), and a round still running when the next is due is not doubled.
// When the set of conditions changes, one that has left it loses its data and mark
// (onlySessions); the others keep theirs. What it holds outlives the component
// (keptTelemetry): mounted again, it starts from there, and the requests in flight when it
// unmounts are cancelled.
//   sessionIds: the live conditions' session ids.
// Resolves to (nextTelemetry)
//   eventsBySession: per session id, once that request has answered: user id -> their events
//     there, in time order;
//   rosterBySession: per session id, once that request has answered;
//   fetchedAtBySession: per session id, when the telemetry held was fetched (its request
//     sent) - the class as of then, however long ago that is;
//   failedBySession: per session id, whether its last round failed (telemetry or roster);
//   lastIdBySession: per session id, the mark of the telemetry held;
//   fullAtBySession: per session id, when its telemetry was last fetched in full (sent);
//   loaded: once a round has had an answer.
export const useConditionTelemetry = (sessionIds) => {
  // The set, in any order.
  const key = [...new Set(sessionIds.map(Number))].sort((a, b) => a - b).join(',')
  // From what was kept of these conditions, if anything.
  const [data, setData] = useState(() => onlySessions(keptTelemetry, idsOf(key)))
  // `data` as of the last round or change of conditions, before React re-renders with it: the
  // next round sends the marks of what it holds.
  const held = useRef(data)

  useEffect(() => {
    const ids = idsOf(key)
    const commit = (next) => {
      if (next === held.current) return
      held.current = next
      keptTelemetry = next
      setData(next)
    }
    commit(onlySessions(held.current, ids))
    if (!ids.length) return undefined
    // Aborted when the component unmounts or the set changes: the requests in flight stop.
    const controller = new AbortController()
    const { signal } = controller
    let running = false
    // The conditions whose last request asked for all of their telemetry and failed (afterIdFor).
    const fullFailed = new Set()

    const load = async () => {
      if (running || (held.current.loaded && document.visibilityState === 'hidden')) return
      running = true
      const sentAt = Date.now()
      try {
        const answers = await Promise.all(
          ids.map(async (id) => {
            const afterId = afterIdFor(held.current, id, sentAt, fullFailed.has(id))
            const [telemetry, roster] = await Promise.all([
              axiosInstance
                .get(`${API}/telemetry/session/${id}/overview?afterId=${afterId}`, { timeout: 30000, signal })
                .then((response) => overviewAnswer(response.data, afterId), () => null),
              axiosInstance
                .get(`${API}/users/session/${id}`, { signal })
                .then((response) => (Array.isArray(response.data) ? response.data : null), () => null),
            ])
            return { id, events: telemetry?.events || null, afterId, lastId: telemetry?.lastId ?? null, roster }
          })
        )
        if (signal.aborted) return
        answers.forEach(({ id, events, afterId }) => {
          if (!afterId && !events) fullFailed.add(id)
          else fullFailed.delete(id)
        })
        commit(nextTelemetry(held.current, { answers, sentAt }))
      } finally {
        running = false
      }
    }
    // Back on the page (from another tab, or a minimized window): a round at once, not at the
    // next tick - until it answers, the Overview shows the class as of before it was hidden.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') load()
    }

    load()
    const timer = setInterval(load, OVERVIEW_POLL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      controller.abort()
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [key])

  return data
}
